/**
 * API e stato server del titolo del giorno (SPEC-020, F-120).
 *
 * ⚠️ `maxExposureAmount` e `maxPortfolioPercentage` sono **stringhe** decimali
 * (ADR-0006): arrivano così e ripartono così. Un `parseFloat` qui riporterebbe
 * dentro l'errore di precisione che tutto il resto del progetto evita.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AssetDomain } from '@/lib/asset-domain';

export type DailyPickMode = 'agentic' | 'oneshot';
export type DailyPickRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export type DailyPickSettings = {
  /** L'ambito della scheda (SPEC-026 §6): una riga di impostazioni per ciascuno. */
  domain: AssetDomain;
  enabled: boolean;
  autoWishlistEnabled: boolean;
  runAtLocalTime: string;
  timezone: string;
  mode: DailyPickMode;
  maxPicks: number;
  minConfidence: number;
  portfolioId: string | null;
  maxExposureAmount: string | null;
  maxPortfolioPercentage: string | null;
};

export type DailyPickEvidence = { claim: string; sourceUrl: string };

export type DailyPickResult = {
  id: string;
  rank: number;
  name: string;
  tickerHint: string | null;
  exchangeHint: string | null;
  isinHint: string | null;
  currencyHint: string | null;
  description: string | null;
  catalystWhat: string;
  catalystDate: string | null;
  catalystTime: string | null;
  catalystSourceUrl: string | null;
  assessment: string | null;
  whatToWatch: string[];
  risks: string[];
  confidenceLevel: 'bassa' | 'media' | 'alta';
  confidenceScore: number;
  confidenceRationale: string | null;
  evidence: DailyPickEvidence[];
  /** `null` = il cancello di risoluzione non lo ha agganciato: non è azionabile. */
  instrumentId: string | null;
  resolutionStatus: string | null;
  resolutionNote: string | null;
  wishlistItemId: string | null;
  /** Perche' NON e' in wishlist, in parole (F-180); `null` se promosso o mai tentato. */
  promotionNote: string | null;
  instrument: { id: string; name: string; symbol: string | null; trading212Ticker: string | null } | null;
};

export type DailyPickMove = { step: number; tool: string; args: Record<string, unknown>; outcome: string };

export type DailyPickStats = {
  mode?: string;
  searches?: number;
  moves?: DailyPickMove[];
  sourcesVisited?: number;
  pagesFetched?: string[];
  promptTokens?: number;
  completionTokens?: number;
};

export type DailyPickRun = {
  id: string;
  tradingDay: string;
  /** L'ambito della scheda (SPEC-026 §6). */
  domain: AssetDomain;
  status: DailyPickRunStatus;
  trigger: 'scheduled' | 'manual';
  mode: DailyPickMode;
  modelId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  /** Perché non ha trovato nulla. Valorizzato solo su un `completed` vuoto. */
  emptyReason: string | null;
  stats: DailyPickStats | null;
  createdAt: string;
  results?: DailyPickResult[];
  resultsCount?: number;
};

const latestKey = (domain: AssetDomain) => ['daily-pick', 'latest', domain];
const runsKey = (domain: AssetDomain) => ['daily-pick', 'runs', domain];

/** La vista «Oggi» dell'ambito: l'ultima esecuzione più le impostazioni, in una sola andata. */
export function useDailyPickLatest(domain: AssetDomain) {
  return useQuery({
    queryKey: latestKey(domain),
    queryFn: () => api.get<{ run: DailyPickRun | null; settings: DailyPickSettings }>(`/daily-pick/latest?domain=${domain}`),
    // Mentre un'esecuzione è in corso la pagina si aggiorna da sola: dura
    // minuti, e chiedere all'utente di ricaricare sarebbe chiedergli di
    // indovinare quando.
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return status === 'queued' || status === 'running' ? 10_000 : false;
    },
  });
}

export function useDailyPickRuns(domain: AssetDomain) {
  return useQuery({
    queryKey: runsKey(domain),
    queryFn: () => api.get<{ items: DailyPickRun[]; total: number }>(`/daily-pick/runs?domain=${domain}`),
  });
}

export function useDailyPickRun(runId: string | undefined) {
  return useQuery({
    queryKey: ['daily-pick', 'run', runId],
    queryFn: () => api.get<DailyPickRun>(`/daily-pick/runs/${runId}`),
    enabled: Boolean(runId),
  });
}

export type CatalogHit = {
  id: string;
  name: string;
  symbol: string | null;
  isin: string | null;
  currency: string | null;
};

/**
 * Ricerca nel catalogo, per l'aggancio manuale (F-128).
 *
 * Riusa `GET /instruments`, che e' la ricerca a catalogo gia' esistente:
 * inventarne una seconda avrebbe voluto dire due definizioni di «cosa e' a
 * catalogo». Sotto i due caratteri non si chiede niente al server — una query a
 * ogni tasto premuto restituirebbe mezzo catalogo.
 */
export function useCatalogSearch(query: string) {
  return useQuery({
    queryKey: ['instruments', 'search', query],
    queryFn: () => api.get<{ items: CatalogHit[] }>(`/instruments?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 2,
  });
}

/**
 * Aggancia a mano un risultato a un titolo a catalogo (F-128).
 *
 * ⚠️ Non crea strumenti: il caso e' «il catalogo lo conosce e l'aggancio
 * automatico non l'ha trovato». Se il titolo non fosse a catalogo servirebbe la
 * riconciliazione della scoperta tematica, che e' un gesto diverso.
 */
/** L'esito dei passi che seguono l'aggancio (F-166): ticker, watchlist, wishlist e tag. */
export type LinkPromotion = {
  tickerAssigned: boolean;
  wishlisted: boolean;
  /** Perche' NON e' in wishlist; `null` quando lo e'. */
  reason: string | null;
};

export function useLinkDailyPickResult(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // F-166: l'aggancio e' un'operazione sola. Il server, dopo aver scritto
    // `instrument_id`, promuove la riga come farebbe l'aggancio automatico —
    // senza soglia — e racconta l'esito accanto al run riletto.
    mutationFn: (input: { resultId: string; instrumentId: string }) =>
      api.patch<DailyPickRun & { promotion: LinkPromotion }>(`/daily-pick/runs/${runId}/results/${input.resultId}`, {
        instrumentId: input.instrumentId,
      }),
    onSuccess: () => {
      // Sia «Oggi» sia lo storico mostrano la stessa riga: invalidare una sola
      // delle due lascerebbe l'altra a raccontare lo stato di prima.
      // Tutti gli ambiti: l'aggancio non sa da quale scheda arriva.
      void queryClient.invalidateQueries({ queryKey: ['daily-pick', 'latest'] });
      void queryClient.invalidateQueries({ queryKey: ['daily-pick', 'runs'] });
      void queryClient.invalidateQueries({ queryKey: ['daily-pick', 'run'] });
    },
  });
}

export function useUpdateDailyPickSettings(domain: AssetDomain) {
  const queryClient = useQueryClient();
  return useMutation({
    // `domain` non viaggia nel corpo: e' la chiave della riga, sta nella query.
    mutationFn: ({ domain: _domain, ...input }: Partial<DailyPickSettings>) =>
      api.put<DailyPickSettings>(`/daily-pick/settings?domain=${domain}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: latestKey(domain) });
    },
  });
}

export function useRunDailyPickNow(domain: AssetDomain) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ runId: string; created: boolean; tradingDay: string; domain: AssetDomain }>(`/daily-pick/runs?domain=${domain}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: latestKey(domain) });
      void queryClient.invalidateQueries({ queryKey: runsKey(domain) });
    },
  });
}
